/**
 * GENERATION & IMPACT → EXECUTIVE DECISION contribution.
 *
 * Until now the promoted scenario reached the go/no-go decision only
 * LAUNDERED THROUGH PROJECT INTENT: promotion rewrites the target phase's
 * `powerSourceConfig.componentParams[].capacityMW`, and the rollup's firm-
 * capacity verdict reads `intent.phases` alone. Everything that makes the
 * scenario a decision — the N-k load and reserve margins, the joint capacity
 * screen, and the solver's own red resource risks — was stripped on the way.
 *
 * This module carries those forward as ATTRIBUTION, deliberately not as a
 * score term:
 *
 *  - It NEVER moves a band. Weighting generation against the other decision
 *    pillars is a product judgement, and silently re-banding existing sites
 *    would be the wrong way to make it. The rollup consumes this the same way
 *    it consumes the unmeasured-diligence note — a named binding constraint
 *    that explains, rather than a cap that lowers.
 *  - It refuses to speak on a STALE snapshot. The caller resolves the promoted
 *    scenario through `resolvePromotedGenerationScenario`, which returns null
 *    unless the id and both integrity hashes still match the intent stamps.
 *  - It reads only frozen values. Nothing here re-solves; a statement always
 *    reflects what the scenario said when it was promoted.
 */
import type {
  EngineeringVerdict,
  GenerationCapacityScreenVerdict,
  GenerationImpactScenarioSnapshot,
} from '../../types/generationImpact';
import { resolveCapacityScreenVerdict } from './screeningIntegrity';

export interface GenerationDecisionContribution {
  scenarioName: string;
  criterionLabel: string;
  /** Joint load + reserve screen — 'conditional' is a caution, never a pass. */
  screenVerdict: GenerationCapacityScreenVerdict;
  /** Deterministic sentence from the screening-integrity resolver. */
  screenStatement: string;
  loadMarginMW: number;
  reserveMarginMW: number;
  /**
   * Decision-facing notes, already phrased for `bindingConstraints`. Empty when
   * the promoted scenario clears its own screen and carries no red risk — in
   * which case generation contributes nothing and says nothing.
   */
  notes: string[];
}

/**
 * Project the promoted scenario into decision-facing notes.
 *
 * Returns null when there is no current promoted scenario, so an absent or
 * stale generation basis leaves the decision exactly as it was.
 */
export function deriveGenerationDecisionContribution(
  promoted: GenerationImpactScenarioSnapshot | null | undefined,
): GenerationDecisionContribution | null {
  if (!promoted?.result?.capacity) return null;
  const capacity = promoted.result.capacity;
  const outages = Number.isInteger(capacity.contingencyOutages) && capacity.contingencyOutages >= 0
    ? capacity.contingencyOutages
    : 1;
  const criterionLabel = outages === 0 ? 'N' : `N-${outages}`;
  const loadMarginMW = Number.isFinite(capacity.contingencyMarginMW)
    ? capacity.contingencyMarginMW
    : capacity.nMinusOneMarginMW;
  const reserveMarginMW = Number.isFinite(capacity.contingencyReserveMarginMW)
    ? capacity.contingencyReserveMarginMW
    : capacity.nMinusOneReserveMarginMW;
  const loadVerdict: EngineeringVerdict = capacity.contingencyVerdict ?? capacity.nMinusOneVerdict;
  const reserveVerdict: EngineeringVerdict = capacity.contingencyReserveVerdict
    ?? capacity.nMinusOneReserveVerdict;

  const screen = resolveCapacityScreenVerdict({
    criterion: criterionLabel,
    loadVerdict,
    reserveVerdict,
    loadMarginMW,
    reserveMarginMW,
  });

  const notes: string[] = [];
  if (screen.screenVerdict !== 'pass') {
    notes.push(`On-site generation — ${screen.statement} (promoted scenario "${promoted.name}").`);
  }
  // The solver's own red risks are already deterministic, already frozen, and
  // already phrased for a human. Amber risks are advisory by design and stay
  // out of the decision surface.
  for (const risk of promoted.result.risks ?? []) {
    if (risk.severity !== 'red') continue;
    notes.push(`On-site generation — ${risk.message}`);
  }

  return {
    scenarioName: promoted.name,
    criterionLabel,
    screenVerdict: screen.screenVerdict,
    screenStatement: screen.statement,
    loadMarginMW,
    reserveMarginMW,
    notes,
  };
}
